import { useEffect, useState, useCallback } from "react";
import { publicApi, saveEval, loadEval, clearEval } from "@/lib/publicApi";
import { apiError, API } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShieldCheck, UploadCloud, FileText, CheckCircle2, AlertTriangle, Loader2, ArrowRight,
  Lock, Sparkles, ScanLine, Copy, Download, Phone, Scale, Star, RefreshCw,
} from "lucide-react";

const DOC_SLOTS = [
  { role: "policy", title: "Policy Copy", hint: "Defines your coverage", required: true },
  { role: "insurer_letter", title: "Rejection / Short-settlement Letter", hint: "The insurer's decision", required: true },
  { role: "claim_file", title: "Claim File", hint: "What you submitted (optional)", required: false },
];

const SECTIONS = [
  ["subject", "Subject"], ["background_facts", "Background Facts"], ["policy_position", "Policy-Based Position"],
  ["legal_arguments", "Legal Position (Indian Consumer Law)"], ["procedural_unfairness", "Procedural Fairness"],
  ["reconsideration_request", "Request for Reconsideration"], ["reserved_rights_escalation", "Reserved Rights & Further Steps"],
  ["signature_block", "Signature"], ["annexures", "Annexures"],
];

const MERIT = {
  challengeable: { label: "Looks Challengeable", cls: "bg-success/10 text-success border-success/30" },
  ambiguous: { label: "Ambiguous — needs judgement", cls: "bg-warning/10 text-warning border-warning/30" },
  substantially_valid: { label: "Narrow grounds only", cls: "bg-destructive/10 text-destructive border-destructive/30" },
};

function PublicHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-3.5">
        <a href="https://accurateclaims.in/"><Brand /></a>
        <a href="https://accurateclaims.in/" className="text-sm font-medium text-muted-foreground hover:text-primary">accurateclaims.in</a>
      </div>
    </header>
  );
}

function Stepper({ step }) {
  const items = ["Details", "Documents", "Evaluation", "Unlock"];
  return (
    <div className="mb-8 flex items-center justify-center gap-2 sm:gap-3">
      {items.map((t, i) => (
        <div key={t} className="flex items-center gap-2 sm:gap-3">
          <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${step >= i ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground"}`}>{i + 1}</div>
          <span className={`hidden text-sm font-medium sm:inline ${step >= i ? "text-primary" : "text-muted-foreground"}`}>{t}</span>
          {i < items.length - 1 && <div className="h-px w-5 bg-border sm:w-8" />}
        </div>
      ))}
    </div>
  );
}

/* ---------------- Payment ---------------- */
function PaymentPanel({ caseId, token, pkg, onPaid, onCancel }) {
  const [info, setInfo] = useState(null);
  const [utr, setUtr] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    publicApi.get(`/public/cases/${caseId}/payment`, { params: { package: pkg, token } })
      .then((r) => setInfo(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [caseId, token, pkg]);

  const confirm = async () => {
    if (utr.trim().length < 8) { toast.error("Enter the UPI reference / UTR number from your payment app"); return; }
    setSubmitting(true);
    try {
      await publicApi.post(`/public/cases/${caseId}/confirm-payment`, { package: pkg, utr, phone },
        { headers: { "X-Case-Token": token } });
      toast.success("Payment recorded — unlocking your result");
      onPaid();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (!info) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <Card className="overflow-hidden rounded-2xl border-border shadow-sm" data-testid="payment-panel">
      <div className="bg-primary px-6 py-5 text-primary-foreground">
        <div className="flex items-center gap-2 text-sm font-semibold"><Lock className="h-4 w-4" /> Secure UPI Payment</div>
        <div className="mt-1 font-heading text-2xl font-bold">{info.label}</div>
      </div>
      <div className="grid gap-6 p-6 md:grid-cols-2">
        <div className="flex flex-col items-center text-center">
          <img src={info.qr} alt="UPI QR code" className="h-56 w-56 rounded-xl border border-border p-2" data-testid="upi-qr" />
          <p className="mt-2 text-xs text-muted-foreground">Scan with any UPI app — amount is pre-filled</p>
          <a href={info.upi_link} className="mt-3 w-full sm:hidden" data-testid="open-upi-app">
            <Button className="w-full gap-2 bg-accent text-accent-foreground hover:bg-accent/90"><ScanLine className="h-4 w-4" />Pay in UPI app</Button>
          </a>
          <button onClick={() => { navigator.clipboard.writeText(info.upi_vpa); toast.success("UPI ID copied"); }}
            className="mt-3 flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="copy-upi">
            <Copy className="h-3.5 w-3.5" /> {info.upi_vpa}
          </button>
          <p className="mt-1 text-xs text-muted-foreground">Payee: {info.payee_name}</p>
        </div>

        <div className="flex flex-col">
          <div className="rounded-xl bg-secondary p-4 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{info.label}</span><span>₹{info.base.toLocaleString("en-IN")}</span></div>
            {info.gst > 0 && <div className="mt-1 flex justify-between"><span className="text-muted-foreground">GST (18%)</span><span>₹{info.gst.toLocaleString("en-IN")}</span></div>}
            <div className="mt-2 flex justify-between border-t border-border pt-2 font-bold text-primary"><span>Total payable</span><span data-testid="pay-total">₹{info.total.toLocaleString("en-IN")}</span></div>
          </div>

          <div className="mt-4 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="utr">UPI Reference / UTR number *</Label>
              <Input id="utr" data-testid="utr-input" placeholder="e.g. 4531 7788 2210" value={utr} onChange={(e) => setUtr(e.target.value)} />
              <p className="text-xs text-muted-foreground">Find this in your payment app after paying (Transaction ID / UTR).</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pphone">Phone (for confirmation)</Label>
              <Input id="pphone" data-testid="pay-phone-input" placeholder="Mobile number" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>

          <div className="mt-auto flex gap-2 pt-4">
            <Button variant="ghost" onClick={onCancel} data-testid="pay-cancel">Back</Button>
            <Button onClick={confirm} disabled={submitting} data-testid="confirm-payment-btn" className="flex-1 gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} I've paid — unlock
            </Button>
          </div>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">Our team verifies every UPI payment against our bank records.</p>
        </div>
      </div>
    </Card>
  );
}

/* ---------------- Main ---------------- */
export default function Evaluate() {
  const [step, setStep] = useState(0); // 0 details, 1 upload, 2 teaser, 3 result
  const [caseRef, setCaseRef] = useState(loadEval());
  const [form, setForm] = useState({ email: "", name: "", phone: "", title: "", insurer_name: "", policy_number: "", claim_number: "", notes: "" });
  const [docs, setDocs] = useState({});
  const [teaser, setTeaser] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [paying, setPaying] = useState(null); // null | 'ai_analysis' | 'consultancy'

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const resume = useCallback(async (ref) => {
    try {
      const { data } = await publicApi.get(`/public/cases/${ref.id}`, { params: { token: ref.token } });
      if (data.paid) { setStep(3); loadResult(ref); }
      else if (data.has_analysis) { setTeaser(data.teaser); setStep(2); }
      else { setStep(1); }
    } catch { clearEval(); setCaseRef(null); setStep(0); }
  }, []);

  useEffect(() => { if (caseRef?.id) resume(caseRef); /* eslint-disable-next-line */ }, []);

  const loadResult = async (ref) => {
    try {
      const { data } = await publicApi.get(`/public/cases/${ref.id}/result`, { params: { token: ref.token } });
      setResult(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const createCase = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await publicApi.post("/public/cases", form);
      const ref = { id: data.id, token: data.token };
      setCaseRef(ref); saveEval(ref.id, ref.token);
      setStep(1);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadDoc = async (role, file) => {
    const fd = new FormData();
    fd.append("doc_role", role); fd.append("file", file);
    try {
      const { data } = await publicApi.post(`/public/cases/${caseRef.id}/documents`, fd, { headers: { "X-Case-Token": caseRef.token } });
      setDocs((d) => ({ ...d, [role]: data }));
      if (data.extraction_warning) toast.warning(data.extraction_warning);
      else toast.success(`${file.name} · ${data.chars} characters read`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await publicApi.post(`/public/cases/${caseRef.id}/analyze`, {}, { headers: { "X-Case-Token": caseRef.token } });
      setTeaser(data.teaser); setStep(2);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const hasRequired = DOC_SLOTS.filter((s) => s.required).every((s) => docs[s.role]);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="mx-auto max-w-5xl px-6 py-10">
        {step < 3 && <Stepper step={step} />}

        {/* STEP 0 — details */}
        {step === 0 && (
          <div className="mx-auto max-w-2xl">
            <div className="text-center">
              <Badge className="mb-3 rounded-full bg-accent/10 text-accent hover:bg-accent/10">AI Claim Evaluation</Badge>
              <h1 className="font-heading text-4xl font-bold text-primary">Challenge a wrongful claim decision</h1>
              <p className="mt-3 text-base text-muted-foreground">Upload your documents and our AI builds a fact-based challenge letter under Indian consumer-law principles. Pay only when you're ready to unlock the result.</p>
            </div>
            <form onSubmit={createCase} className="mt-8 space-y-5" data-testid="eval-details-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2"><Label htmlFor="name">Your name *</Label><Input id="name" required data-testid="eval-name" value={form.name} onChange={upd("name")} /></div>
                <div className="space-y-2"><Label htmlFor="email">Email *</Label><Input id="email" type="email" required data-testid="eval-email" value={form.email} onChange={upd("email")} /></div>
                <div className="space-y-2"><Label htmlFor="phone">Phone</Label><Input id="phone" data-testid="eval-phone" value={form.phone} onChange={upd("phone")} /></div>
                <div className="space-y-2"><Label htmlFor="insurer">Insurer</Label><Input id="insurer" data-testid="eval-insurer" placeholder="e.g. Star Health" value={form.insurer_name} onChange={upd("insurer_name")} /></div>
              </div>
              <div className="space-y-2"><Label htmlFor="title">What is this claim about? *</Label><Input id="title" required data-testid="eval-title" placeholder="e.g. Mediclaim rejected as pre-existing" value={form.title} onChange={upd("title")} /></div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2"><Label htmlFor="policy">Policy number</Label><Input id="policy" data-testid="eval-policy" value={form.policy_number} onChange={upd("policy_number")} /></div>
                <div className="space-y-2"><Label htmlFor="claim">Claim number</Label><Input id="claim" data-testid="eval-claim" value={form.claim_number} onChange={upd("claim_number")} /></div>
              </div>
              <Button type="submit" disabled={busy} data-testid="eval-start-btn" className="w-full gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Continue to upload
              </Button>
              <div className="flex items-center justify-center gap-6 pt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-accent" />Independent · IRDAI-aware</span>
                <span className="flex items-center gap-1"><Lock className="h-3.5 w-3.5 text-accent" />Documents kept confidential</span>
              </div>
            </form>
          </div>
        )}

        {/* STEP 1 — upload */}
        {step === 1 && (
          <div className="mx-auto max-w-2xl space-y-5">
            <div className="text-center">
              <h1 className="font-heading text-3xl font-bold text-primary">Upload your documents</h1>
              <p className="mt-2 text-sm text-muted-foreground">We read only what's in your files. Nothing is invented.</p>
            </div>
            <div className="grid gap-4">
              {DOC_SLOTS.map((slot) => (
                <Card key={slot.role} className="rounded-xl border-border p-5 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 font-medium text-primary"><FileText className="h-4 w-4 text-accent" />{slot.title}{slot.required && <span className="text-accent">*</span>}</div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{slot.hint}</p>
                    </div>
                    {docs[slot.role] && <CheckCircle2 className="h-5 w-5 text-success" />}
                  </div>
                  {docs[slot.role] ? (
                    <div className="mt-3 rounded-lg bg-secondary p-3 text-xs">
                      <div className="font-medium text-primary">{docs[slot.role].original_filename}</div>
                      <div className="mt-1 text-muted-foreground">{docs[slot.role].extraction_warning
                        ? <span className="flex items-center gap-1 text-warning"><AlertTriangle className="h-3 w-3" />{docs[slot.role].extraction_warning}</span>
                        : `${docs[slot.role].chars} characters extracted`}</div>
                    </div>
                  ) : (
                    <label className="mt-3 flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-background p-6 text-center hover:border-accent/50">
                      <UploadCloud className="h-6 w-6 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">PDF, DOCX, image or text</span>
                      <input type="file" className="hidden" accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp"
                        onChange={(e) => e.target.files?.[0] && uploadDoc(slot.role, e.target.files[0])} data-testid={`eval-upload-${slot.role}`} />
                    </label>
                  )}
                </Card>
              ))}
            </div>
            <Button onClick={runAnalysis} disabled={!hasRequired || analyzing} data-testid="eval-analyze-btn" className="w-full gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
              {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scale className="h-4 w-4" />}{analyzing ? "Evaluating your claim…" : "Evaluate my claim"}
            </Button>
            {!hasRequired && <p className="text-center text-xs text-muted-foreground">Upload the policy copy and the insurer's letter to continue.</p>}
          </div>
        )}

        {/* STEP 2 — teaser + paywall */}
        {step === 2 && teaser && (
          <div className="mx-auto max-w-3xl">
            {!paying ? (
              <>
                <div className="text-center">
                  <h1 className="font-heading text-3xl font-bold text-primary">Your evaluation is ready</h1>
                  <p className="mt-2 text-sm text-muted-foreground">Here's a preview. Unlock the full report and ready-to-send challenge letter below.</p>
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  <Card className="rounded-xl p-5 text-center"><div className="text-xs font-semibold uppercase text-muted-foreground">Case type</div><div className="mt-1 font-heading text-lg font-bold capitalize text-primary">{(teaser.case_type || "").replace("_", " ") || "—"}</div></Card>
                  <Card className="rounded-xl p-5 text-center"><div className="text-xs font-semibold uppercase text-muted-foreground">Assessment</div>{teaser.merit_assessment && <Badge variant="outline" className={`mt-2 ${MERIT[teaser.merit_assessment]?.cls}`}>{MERIT[teaser.merit_assessment]?.label}</Badge>}</Card>
                  <Card className="rounded-xl p-5 text-center"><div className="text-xs font-semibold uppercase text-muted-foreground">Confidence</div><div className="mt-1 font-heading text-lg font-bold capitalize text-primary">{teaser.confidence || "—"}</div></Card>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-4">
                  {[["claim_facts", "Facts found"], ["insurer_grounds", "Insurer grounds"], ["missing_info", "Gaps flagged"], ["contradictions", "Contradictions"]].map(([k, l]) => (
                    <Card key={k} className="rounded-xl p-4 text-center"><div className="font-heading text-2xl font-bold text-accent">{teaser.counts?.[k] ?? 0}</div><div className="text-xs text-muted-foreground">{l}</div></Card>
                  ))}
                </div>

                <Card className="relative mt-6 overflow-hidden rounded-2xl border-accent/30">
                  <div className="pointer-events-none select-none p-6 blur-sm" aria-hidden>
                    <h3 className="font-heading text-lg font-semibold text-primary">Draft Challenge Letter</h3>
                    <p className="mt-2 text-sm text-slate-500">Subject: Representation seeking reconsideration of repudiation of Claim No. ••••• under Policy No. ••••• — the full fact-bound letter with policy position, consumer-law arguments and escalation path is ready…</p>
                    <p className="mt-2 text-sm text-slate-400">••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••</p>
                  </div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px]">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground"><Lock className="h-5 w-5" /></div>
                    <div className="mt-3 font-heading text-xl font-bold text-primary">Unlock your full report</div>
                    <div className="mt-1 text-sm text-muted-foreground">Full letter + Word export + review notes</div>
                    <Button onClick={() => setPaying("ai_analysis")} data-testid="unlock-btn" className="mt-4 gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
                      <Sparkles className="h-4 w-4" /> Unlock for ₹1,179 <span className="text-xs opacity-80">(₹999 + GST)</span>
                    </Button>
                  </div>
                </Card>
              </>
            ) : (
              <PaymentPanel caseId={caseRef.id} token={caseRef.token} pkg="ai_analysis"
                onCancel={() => setPaying(null)}
                onPaid={() => { setPaying(null); setStep(3); loadResult(caseRef); }} />
            )}
          </div>
        )}

        {/* STEP 3 — result */}
        {step === 3 && (
          <div className="mx-auto max-w-3xl">
            {!result ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className="rounded-full bg-success/10 text-success hover:bg-success/10"><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Unlocked</Badge>
                    <h1 className="mt-2 font-heading text-3xl font-bold text-primary">Your challenge letter</h1>
                  </div>
                  {result.draft && (
                    <a href={`${API}/public/cases/${caseRef.id}/export?token=${encodeURIComponent(caseRef.token)}`} data-testid="eval-export-btn">
                      <Button className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90"><Download className="h-4 w-4" />Download Word</Button>
                    </a>
                  )}
                </div>

                {result.analysis?.review_warnings?.length > 0 && (
                  <Card className="mt-4 rounded-xl border-warning/40 bg-warning/10 p-4">
                    <div className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                      <div><div className="text-sm font-semibold text-primary">Please review before sending</div>
                        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-primary/80">{result.analysis.review_warnings.slice(0, 4).map((w, i) => <li key={i}>{w}</li>)}</ul>
                      </div></div>
                  </Card>
                )}

                {result.draft && (
                  <div className="paper mt-5 rounded-xl p-8 text-sm leading-relaxed text-slate-800" data-testid="eval-letter">
                    <p className="text-center text-xs font-semibold tracking-widest text-accent">WITHOUT PREJUDICE</p>
                    {result.draft.subject && <p className="mt-5 font-semibold text-primary">Subject: {result.draft.subject}</p>}
                    {SECTIONS.filter(([k]) => k !== "subject").map(([k, label]) => result.draft[k] ? (
                      <div key={k} className="mt-4">
                        <h4 className="font-heading text-base font-semibold text-primary">{label}</h4>
                        {String(result.draft[k]).split("\n").map((p, i) => p.trim() && <p key={i} className="mt-2 whitespace-pre-wrap">{p}</p>)}
                      </div>
                    ) : null)}
                  </div>
                )}

                {/* Consultancy upsell */}
                {!result.consultancy_booked ? (
                  paying === "consultancy" ? (
                    <div className="mt-6"><PaymentPanel caseId={caseRef.id} token={caseRef.token} pkg="consultancy"
                      onCancel={() => setPaying(null)}
                      onPaid={() => { setPaying(null); loadResult(caseRef); }} /></div>
                  ) : (
                    <Card className="mt-6 overflow-hidden rounded-2xl border-gold/40 bg-gold/5">
                      <div className="flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <div className="flex items-center gap-2"><Star className="h-4 w-4 text-gold" /><span className="text-xs font-semibold uppercase tracking-wide text-gold">Recommended next step</span></div>
                          <h3 className="mt-1 font-heading text-xl font-bold text-primary">Add expert consultancy — ₹1,499</h3>
                          <p className="mt-1 max-w-md text-sm text-muted-foreground">A senior Accurate Claims advisor reviews your letter, strengthens your file and represents you with the insurer. Far cheaper than standard consultancy fees.</p>
                        </div>
                        <Button onClick={() => setPaying("consultancy")} data-testid="book-consultancy-btn" className="shrink-0 gap-2 bg-primary text-primary-foreground hover:bg-primary/90"><Phone className="h-4 w-4" />Book for ₹1,499</Button>
                      </div>
                    </Card>
                  )
                ) : (
                  <Card className="mt-6 rounded-2xl border-success/40 bg-success/5 p-6 text-center">
                    <CheckCircle2 className="mx-auto h-8 w-8 text-success" />
                    <h3 className="mt-2 font-heading text-xl font-bold text-primary">Consultancy booked</h3>
                    <p className="mt-1 text-sm text-muted-foreground">Our team will contact you shortly to begin your representation.</p>
                  </Card>
                )}

                <button onClick={() => { clearEval(); window.location.reload(); }} className="mx-auto mt-8 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="eval-new-btn">
                  <RefreshCw className="h-3.5 w-3.5" /> Start another evaluation
                </button>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
